import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, Mail, Crown, Trash2, KeyRound, Key, Upload, Download, Star,
  Search, Save, AlertCircle, X, ChevronDown, UserPlus, Pencil, RotateCcw, AtSign, Send, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { formatDistanceToNow } from "date-fns";
import SignupVisibilityCheck from "@/components/SignupVisibilityCheck";
import RosterNoticeDialog from "@/components/RosterNoticeDialog";
import OrphanAccounts from "@/components/OrphanAccounts";

function SortHeader({ field, label, sortField, sortDir, onClick }) {
  const active = sortField === field;
  return (
    <th className="px-3 py-2 cursor-pointer select-none hover:text-slate-900" onClick={() => onClick(field)} data-testid={`roster-sort-${field}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-[10px]">{sortDir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}

function MasterExportFreshness() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/admin/master-export/status")
        .then(({ data }) => { if (!cancelled) setStatus(data); })
        .catch(() => { if (!cancelled) setStatus({}); });
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!status) return null;
  const stamp = status.last_attendance_update;
  return (
    <div className="text-[10px] text-[#525252] tracking-wider uppercase self-center pl-2" data-testid="roster-master-freshness">
      {stamp
        ? `Data as of ${formatDistanceToNow(new Date(stamp))} ago · live`
        : "Live · no attendance saved yet"}
    </div>
  );
}

const TEAM_LETTERS = ["A", "B", "C", "D", "E", "F"];
const REQUIRED_MAPPING_KEYS = ["first_name", "last_name"];

export default function Roster() {
  const [roster, setRoster] = useState([]);
  const [orphanKey, setOrphanKey] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [teamCount, setTeamCount] = useState(3);
  const [teams, setTeams] = useState([]);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [leaderDialogOpen, setLeaderDialogOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importDecisions, setImportDecisions] = useState({});
  const [lastUpload, setLastUpload] = useState(null);
  const [editEntry, setEditEntry] = useState(null); // null = closed, {} = add new, {...row} = edit
  const [sortField, setSortField] = useState("team");   // team | name | email | prid | dept | campus | status
  const [sortDir, setSortDir] = useState("asc");
  const fileRef = useRef(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rosterRes, teamsRes, progRes, lastRes] = await Promise.all([
        api.get("/admin/roster"),
        api.get("/teams"),
        api.get("/programme"),
        api.get("/admin/roster/last-upload").catch(() => ({ data: null })),
      ]);
      setRoster(rosterRes.data || []);
      setTeams(teamsRes.data || []);
      setTeamCount(progRes.data?.team_count || 3);
      setLastUpload(lastRes?.data && lastRes.data.filename ? lastRes.data : null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load roster");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = !term ? [...roster] : roster.filter((r) =>
      [r.first_name, r.last_name, r.full_name, r.email, r.prid, r.department, r.campus]
        .filter(Boolean).some((v) => v.toLowerCase().includes(term))
    );
    // Default sort = team letter (A..F) then name; unassigned last
    const compare = (a, b) => {
      let av, bv;
      switch (sortField) {
        case "name":  av = (a.full_name || `${a.first_name || ""} ${a.last_name || ""}`).trim().toLowerCase(); bv = (b.full_name || `${b.first_name || ""} ${b.last_name || ""}`).trim().toLowerCase(); break;
        case "email": av = (a.email || "").toLowerCase(); bv = (b.email || "").toLowerCase(); break;
        case "prid":  av = (a.prid || "").toLowerCase(); bv = (b.prid || "").toLowerCase(); break;
        case "dept":  av = (a.department || "").toLowerCase(); bv = (b.department || "").toLowerCase(); break;
        case "campus": av = (a.campus || "").toLowerCase(); bv = (b.campus || "").toLowerCase(); break;
        case "status": av = (a.status || (a.registered ? "registered" : "pending")).toLowerCase(); bv = (b.status || (b.registered ? "registered" : "pending")).toLowerCase(); break;
        case "team":
        default: {
          // Unassigned (empty team_letter) always sorts LAST regardless of dir
          const al = a.team_letter || ""; const bl = b.team_letter || "";
          if (!al && bl) return 1; if (al && !bl) return -1;
          av = al.toLowerCase(); bv = bl.toLowerCase();
          if (av === bv) {
            const an = (a.full_name || `${a.first_name || ""} ${a.last_name || ""}`).trim().toLowerCase();
            const bn = (b.full_name || `${b.first_name || ""} ${b.last_name || ""}`).trim().toLowerCase();
            return an < bn ? -1 : an > bn ? 1 : 0;
          }
        }
      }
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * (sortDir === "asc" ? 1 : -1);
    };
    return base.sort(compare);
  }, [q, roster, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const stats = useMemo(() => {
    const total = roster.length;
    const reg = roster.filter((r) => r.registered).length;
    const assigned = roster.filter((r) => r.team_letter).length;
    return { total, reg, pending: total - reg, unassigned: total - assigned };
  }, [roster]);

  // ---------- Team count ----------
  const setCount = async (c) => {
    if (c === teamCount) return;
    try {
      await api.post("/admin/teams/ensure-count", { count: c });
      toast.success(`Team count set to ${c}`);
      setTeamCount(c);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update team count");
    }
  };

  // ---------- Import CSV / XLSX ----------
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/roster/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(data);
      const defaults = {};
      (data.duplicates || []).forEach((d) => { defaults[d.existing_id] = "update"; });
      setImportDecisions(defaults);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read file");
    }
  };

  const commitImport = async () => {
    try {
      const { data } = await api.post("/admin/roster/import/commit", {
        token: importPreview.token,
        decisions: importDecisions,
      });
      toast.success(`Added ${data.added} · Updated ${data.updated} · Skipped ${data.skipped}`);
      setImportPreview(null);
      setImportDecisions({});
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not commit import");
    }
  };

  const remap = async (nextMapping) => {
    if (!importPreview?.token) return;
    try {
      const { data } = await api.post("/admin/roster/import/remap", {
        token: importPreview.token,
        mapping: nextMapping,
      });
      setImportPreview(data);
      // Reset dup decisions for any dup rows that fell off after remap
      const validIds = new Set((data.duplicates || []).map((d) => d.existing_id));
      setImportDecisions((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => { if (validIds.has(k)) next[k] = prev[k]; });
        return next;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update mapping");
    }
  };

  const downloadConsentExport = async () => {
    try {
      const res = await api.get("/admin/gdpr/consent-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `iteams-consent-register-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Consent register downloaded");
    } catch {
      toast.error("Could not download consent register");
    }
  };

  const eraseStudent = async (r) => {
    const name = r.full_name || `${r.first_name} ${r.last_name}`;
    if (!(await confirm({
      title: `Erase all data for ${name}?`,
      description: "GDPR right to erasure. This permanently deletes their roster row, login, team notes and uploaded files, attendance records, mentor bookings, email history and lockout records. Only an anonymised audit entry (who erased, when, what counts) is kept. This cannot be undone.",
      confirmText: "Erase everything",
      destructive: true,
    }))) return;
    const typed = window.prompt(`Type ERASE to confirm permanent deletion of ${name}'s data`);
    if (typed !== "ERASE") { toast.info("Erasure cancelled"); return; }
    try {
      const { data } = await api.post("/admin/gdpr/erase", { roster_id: r.id });
      const parts = Object.entries(data.removed || {}).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`);
      toast.success(`Erased ${data.name}: ${parts.join(", ") || "roster row"}${data.files_deleted ? `, ${data.files_deleted} files` : ""}`);
      setOrphanKey((k) => k + 1);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erasure failed");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/roster/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "iteams-roster-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  // ---------- Inline edit ----------
  const patchRow = async (rid, patch) => {
    try {
      await api.patch(`/admin/roster/${rid}`, patch);
      setRoster((prev) => prev.map((r) => r.id === rid ? { ...r, ...patch } : r));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
      await load();
    }
  };

  const assignTeamOne = (rid, letter) => patchRow(rid, { team_letter: letter || null });

  const bulkAssignTeam = async (letter) => {
    if (selected.size === 0) return;
    try {
      await api.post("/admin/roster/bulk-assign-team", {
        roster_ids: [...selected],
        team_letter: letter || null,
      });
      toast.success(`Assigned ${selected.size} student${selected.size === 1 ? "" : "s"} to ${letter ? `Team ${letter}` : "unassigned"}`);
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign");
    }
  };

  const rejoin = async (r) => {
    try {
      await api.post(`/admin/roster/${r.id}/undo-withdraw`);
      toast.success(`${r.full_name || "Student"} rejoined — reassign a team when ready`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not undo withdraw");
    }
  };

  const setLeader = async (r) => {
    try {
      await api.post(`/admin/roster/${r.id}/set-leader`, { is_leader: !r.is_leader });
      toast.success(!r.is_leader ? "Set as team leader" : "Team leader cleared");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set leader");
    }
  };

  const changeLoginEmail = async (r, presetEmail) => {
    const name = r.full_name || `${r.first_name} ${r.last_name}`;
    let newEmail = presetEmail;
    if (!newEmail) {
      newEmail = window.prompt(`New login email for ${name}\n(currently ${r.account_email || "unknown"}; must end @essex.ac.uk)`, r.email || "");
      if (newEmail === null) return;
    }
    newEmail = (newEmail || "").trim().toLowerCase();
    if (!/essex\.ac\.uk$/.test(newEmail)) { toast.error("Must be a University of Essex address (ending @essex.ac.uk)"); return; }
    if (!(await confirm({
      title: `Change ${name}'s login email?`,
      description: `${r.account_email || "current email"} → ${newEmail}. Any lockout is cleared and the roster row is linked to this account. They'll sign in with the new address straight away.`,
      confirmText: "Change email",
    }))) return;
    try {
      await api.post(`/admin/roster/${r.id}/change-login-email`, { new_email: newEmail });
      toast.success(`Login email changed to ${newEmail}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change email");
    }
  };

  const nudgeRegister = async (r) => {
    const all = !r;
    const name = r ? (r.full_name || `${r.first_name} ${r.last_name}`) : null;
    if (!all && !r.email) { toast.error("This roster row has no email — add one first"); return; }
    if (!(await confirm({
      title: all ? `Nudge ${stats.pending} student${stats.pending === 1 ? "" : "s"} to register?` : `Nudge ${name} to register?`,
      description: all
        ? "Every roster student without a login yet gets an email with the registration link. Emails are sent at a safe pace in the background."
        : `${r.email} gets an email with the registration link.`,
      confirmText: all ? "Send nudges" : "Send nudge",
    }))) return;
    try {
      const { data } = await api.post("/admin/roster/nudge-register", all ? {} : { only_ids: [r.id] });
      if (data.queued === 0) toast.info(data.message || "Nobody to nudge");
      else toast.success(`Queued ${data.queued} registration nudge${data.queued === 1 ? "" : "s"}${data.skipped?.length ? ` · ${data.skipped.length} skipped (no email)` : ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send nudge");
    }
  };

  const removeEntry = async (r) => {
    const name = r.full_name || `${r.first_name} ${r.last_name}`;
    if (!(await confirm({
      title: `Remove ${name}?`,
      description: r.registered
        ? "This removes them from the roster and unassigns them from any team. You'll be asked next whether to also delete their login."
        : "They'll no longer be selectable on the Register page.",
      confirmText: "Remove",
      destructive: true,
    }))) return;
    let deleteAccount = false;
    if (r.registered) {
      deleteAccount = await confirm({
        title: `Also delete ${name}'s login?`,
        description: `${r.account_email || r.email} can still sign in to the platform until their account is deleted. Choose "Delete login" if they have left the programme, or "Keep login" to leave their access in place.`,
        confirmText: "Delete login",
        cancelText: "Keep login",
        destructive: true,
      });
    }
    try {
      const { data } = await api.delete(`/admin/roster/${r.id}`, { params: { delete_account: deleteAccount } });
      toast.success(data.deleted_account ? `Removed from roster and login deleted (${data.deleted_account})` : "Removed from roster");
      setOrphanKey((k) => k + 1);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const toggleSelect = (rid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  };

  const selectAllVisible = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.id)));
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="roster-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Student Roster</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Import the cohort from the University CRM, assign students to teams A–{TEAM_LETTERS[teamCount-1]},
            and set team leaders. PRID, gender, email and dietary info are visible to admins only.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setEditEntry({})}
            data-testid="roster-add-student-btn"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> Add student
          </Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="roster-download-template-btn">
            <Download className="w-4 h-4 mr-1.5" /> Download template
          </Button>
          <SignupVisibilityCheck />
          <Button variant="outline" onClick={downloadConsentExport} title="CSV of who consented, when, and which wording version" data-testid="roster-consent-export-btn">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Consent register
          </Button>
          <RosterNoticeDialog teamLetters={TEAM_LETTERS.slice(0, teamCount)} roster={roster} />
          <Button
            variant="outline"
            data-testid="roster-relink-students-btn"
            title="Attach registered students who have no team to their roster row and team"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/roster/relink-students");
                const n = data.linked?.length || 0, u = data.unmatched?.length || 0;
                toast.success(`${n} student${n === 1 ? "" : "s"} placed in their team${u ? ` · ${u} unmatched` : ""}`, {
                  description: u ? data.unmatched.map((x) => `${x.name} (${x.reason})`).join(", ") : undefined,
                });
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Could not relink"); }
            }}
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Fix team links
          </Button>
          <Button
            variant="outline"
            data-testid="roster-download-master-btn"
            onClick={async () => {
              try {
                const res = await api.get("/admin/master-export.csv", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "iteams-master-export.csv";
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Master export downloaded");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not download master export");
              }
            }}
          >
            <Download className="w-4 h-4 mr-1.5" /> Master export
          </Button>
          <MasterExportFreshness />
          <Button
            variant="outline"
            data-testid="roster-download-linkedin-btn"
            onClick={async () => {
              try {
                const res = await api.get("/admin/linkedin-recommendations.csv", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "linkedin-recommendations.csv";
                a.click();
                URL.revokeObjectURL(url);
                toast.success("LinkedIn opt-ins downloaded");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not download LinkedIn opt-ins");
              }
            }}
          >
            <Download className="w-4 h-4 mr-1.5" /> LinkedIn opt-ins
          </Button>
          <Button onClick={() => fileRef.current?.click()} data-testid="roster-import-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV / Excel
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={onFilePicked}
            data-testid="roster-file-input"
          />
          <Button
            variant="outline"
            onClick={() => setRenameDialogOpen(true)}
            data-testid="roster-rename-teams-btn"
          >
            <Star className="w-4 h-4 mr-1.5" /> Rename teams
          </Button>
          <Button
            variant="outline"
            onClick={() => setLeaderDialogOpen(true)}
            data-testid="roster-bulk-leader-btn"
          >
            <Crown className="w-4 h-4 mr-1.5" /> Set Team Leaders
          </Button>
        </div>
      </div>

      {/* Team count + stats */}
      <div className="flex flex-wrap gap-4 items-center p-4 rounded-lg border border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Teams in this cohort:</span>
          <div className="inline-flex rounded-md overflow-hidden border border-slate-300 bg-white">
            {[3, 6].map((c) => (
              <button
                key={c}
                onClick={() => setCount(c)}
                data-testid={`roster-team-count-${c}`}
                className={`px-3 py-1.5 text-sm font-semibold border-r last:border-r-0 border-slate-300 ${
                  teamCount === c ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >{c}</button>
            ))}
          </div>
          <span className="text-xs text-slate-500 ml-2">
            Teams A–{TEAM_LETTERS[teamCount - 1]}
          </span>
        </div>
        <div className="flex-1 min-w-[100px]" />
        <StatChip label="Total" value={stats.total} />
        <StatChip label="Registered" value={stats.reg} tone="green" />
        <StatChip label="Pending" value={stats.pending} tone="amber" />
        {stats.pending > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => nudgeRegister(null)}
            className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
            title="Email everyone who hasn't created a login yet"
            data-testid="roster-nudge-all-btn"
          >
            <Send className="w-3 h-3 mr-1" /> Nudge all to register
          </Button>
        )}
        <StatChip label="Unassigned" value={stats.unassigned} tone="red" />
      </div>

      {/* Last upload columns hint — helps admins spot when the CRM changes format */}
      <OrphanAccounts refreshKey={orphanKey} />
      {lastUpload && (
        <div
          className="p-3 rounded-lg border border-indigo-200 bg-indigo-50/60 text-xs text-indigo-900"
          data-testid="roster-last-upload-hint"
        >
          <div className="font-semibold text-indigo-800 mb-1">
            Last upload: <span className="font-mono">{lastUpload.filename}</span>
            {lastUpload.at && (
              <span className="text-indigo-600 font-normal ml-2">
                · {new Date(lastUpload.at).toLocaleString()}
              </span>
            )}
            {typeof lastUpload.row_count === "number" && (
              <span className="text-indigo-600 font-normal ml-2">
                · {lastUpload.row_count} data row{lastUpload.row_count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="text-indigo-800/90">
            Columns detected:{" "}
            {(lastUpload.detected_headers || []).map((h) => (
              <code key={h} className="inline-block mx-0.5 my-0.5 px-1.5 py-0.5 bg-white border border-indigo-200 rounded font-mono">
                {h}
              </code>
            ))}
          </div>
        </div>
      )}

      {/* Search + bulk actions */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, PRID, department…"
            className="pl-9"
            data-testid="roster-search"
          />
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-50 border border-indigo-200 text-sm">
            <span className="text-indigo-700">{selected.size} selected</span>
            <span className="text-indigo-400">·</span>
            <span className="text-indigo-700 text-xs">Bulk assign to:</span>
            {TEAM_LETTERS.slice(0, teamCount).map((L) => (
              <button
                key={L}
                onClick={() => bulkAssignTeam(L)}
                data-testid={`roster-bulk-assign-${L}`}
                className="w-7 h-7 rounded font-bold text-xs bg-white border border-indigo-300 hover:bg-indigo-100"
              >{L}</button>
            ))}
            <button
              onClick={() => bulkAssignTeam("")}
              data-testid="roster-bulk-unassign"
              className="text-xs px-2 py-1 rounded text-indigo-700 hover:bg-indigo-100"
            >Unassign</button>
            <button
              onClick={() => setSelected(new Set())}
              className="ml-1 text-indigo-500 hover:text-indigo-700"
              data-testid="roster-selection-clear"
            ><X className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center text-slate-400 py-12">Loading roster…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-slate-200 p-12 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-medium text-slate-600">The roster is empty.</div>
          <div className="text-sm text-slate-400 mt-1">
            Download the template, fill it in from the University CRM export, and import it.
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    onChange={selectAllVisible}
                    checked={selected.size > 0 && selected.size === filtered.length}
                    data-testid="roster-select-all"
                  />
                </th>
                <SortHeader field="name"   label="Name"   sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader field="email"  label="Email"  sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader field="prid"   label="PRID"   sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader field="dept"   label="Dept"   sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader field="campus" label="Campus" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                <th className="px-3 py-2">Gender</th>
                <SortHeader field="team"   label="Team"   sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                <th className="px-3 py-2">Team Name</th>
                <th className="px-3 py-2">Dietary</th>
                <SortHeader field="status" label="Status" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className={`hover:bg-slate-50 ${r.is_leader ? "bg-amber-50/40" : ""}`}
                  data-testid={`roster-row-${r.id}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleSelect(r.id)}
                      data-testid={`roster-check-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {r.is_leader && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide"
                          title="Team Leader — primary contact"
                          data-testid={`roster-leader-badge-${r.id}`}
                        >
                          <Crown className="w-3 h-3" /> Team Leader
                        </span>
                      )}
                      <button
                        onClick={() => setEditEntry(r)}
                        className="font-medium text-left hover:underline hover:text-indigo-700"
                        data-testid={`roster-edit-${r.id}`}
                      >
                        {r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim()}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">{r.email || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 font-mono text-xs">{r.prid || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.department || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.campus || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.gender || "—"}</td>
                  <td className="px-3 py-2">
                    <select
                      value={r.team_letter || ""}
                      onChange={(e) => assignTeamOne(r.id, e.target.value)}
                      data-testid={`roster-team-select-${r.id}`}
                      className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
                    >
                      <option value="">—</option>
                      {TEAM_LETTERS.slice(0, teamCount).map((L) => (
                        <option key={L} value={L}>{L}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs italic">
                    {r.team_name || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-xs">
                    {r.dietary && r.dietary !== "None" ? (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                        {r.dietary}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {(r.status || "").toLowerCase() === "withdrew" ? (
                      <span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full">
                        Withdrew
                      </span>
                    ) : r.registered ? (
                      <span className="inline-flex flex-col items-start gap-0.5">
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <Mail className="w-3 h-3" /> Registered
                        </span>
                        {r.emails_off && (
                          <span className="text-[10px] text-rose-700 font-semibold" title="This student has withdrawn consent — the platform will not email them" data-testid={`roster-emails-off-${r.id}`}>emails off</span>
                        )}
                        {r.email_mismatch && (
                          <span className="text-[10px] text-amber-700 inline-flex items-center gap-1.5 flex-wrap" title="The student registered with a different email than the roster holds" data-testid={`roster-email-mismatch-${r.id}`}>
                            as {r.account_email}
                            {r.email && (
                              <button type="button" onClick={() => changeLoginEmail(r, r.email)}
                                className="underline font-semibold text-[#0F1F2E] hover:text-[#25AAAA]"
                                title={`Change their login to the roster email ${r.email}`}
                                data-testid={`roster-use-roster-email-${r.id}`}>
                                Use roster email
                              </button>
                            )}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="inline-flex flex-col items-start gap-0.5">
                        <span className="text-slate-400">Not registered</span>
                        {r.nudged_at && (
                          <span className="text-[10px] text-slate-400" data-testid={`roster-nudged-${r.id}`}>
                            nudged {formatDistanceToNow(new Date(r.nudged_at), { addSuffix: true })}{r.nudge_count > 1 ? ` (×${r.nudge_count})` : ""}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!r.registered && (r.status || "").toLowerCase() !== "withdrew" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => nudgeRegister(r)}
                        className="text-amber-700 hover:bg-amber-50"
                        title="Email this student a nudge to create their login"
                        data-testid={`roster-nudge-btn-${r.id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {(r.status || "").toLowerCase() === "withdrew" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => rejoin(r)}
                        title="Undo withdraw — mark student as Registered again (admin must reassign a team)"
                        data-testid={`roster-rejoin-btn-${r.id}`}
                        className="text-emerald-700 hover:bg-emerald-50"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                    {r.registered && (r.status || "").toLowerCase() !== "withdrew" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setLeader(r)}
                        title={r.is_leader ? "Remove Team Leader flag" : "Mark as Team Leader (primary contact)"}
                        data-testid={`roster-leader-btn-${r.id}`}
                      >
                        <Crown className={`w-4 h-4 ${r.is_leader ? "text-amber-600 fill-amber-500" : "text-slate-400"}`} />
                      </Button>
                    )}
                    {r.registered && (r.status || "").toLowerCase() !== "withdrew" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => changeLoginEmail(r)}
                        className="text-slate-500 hover:bg-slate-100"
                        title="Change this student's login email"
                        data-testid={`roster-change-email-btn-${r.id}`}
                      >
                        <AtSign className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => eraseStudent(r)}
                      className="text-slate-400 hover:text-rose-700 hover:bg-rose-50"
                      title="GDPR erasure — permanently delete all of this student's data"
                      data-testid={`roster-erase-btn-${r.id}`}
                    >
                      <ShieldAlert className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditEntry(r)}
                      className="text-slate-500 hover:bg-slate-100"
                      title="Edit student details"
                      data-testid={`roster-edit-btn-${r.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEntry(r)}
                      className="text-rose-600 hover:bg-rose-50"
                      data-testid={`roster-remove-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import preview modal */}
      {importPreview && (
        <ImportPreviewModal
          preview={importPreview}
          decisions={importDecisions}
          setDecisions={setImportDecisions}
          onRemap={remap}
          onCancel={() => { setImportPreview(null); setImportDecisions({}); }}
          onCommit={commitImport}
        />
      )}

      {/* Rename teams modal */}
      {renameDialogOpen && (
        <RenameTeamsDialog
          teams={teams}
          teamCount={teamCount}
          onClose={() => setRenameDialogOpen(false)}
          onSaved={async () => { setRenameDialogOpen(false); await load(); }}
        />
      )}

      {/* Bulk Team Leader dialog */}
      {leaderDialogOpen && (
        <BulkProjectLeadDialog
          teams={teams}
          teamCount={teamCount}
          roster={roster}
          onClose={() => setLeaderDialogOpen(false)}
          onSaved={async () => { setLeaderDialogOpen(false); await load(); }}
        />
      )}

      {/* Add / edit student modal */}
      {editEntry !== null && (
        <RosterEditDialog
          entry={editEntry}
          teamCount={teamCount}
          onClose={() => setEditEntry(null)}
          onSaved={async () => { setEditEntry(null); await load(); }}
        />
      )}
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const toneClass = {
    green: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-800",
  }[tone] || "bg-slate-100 text-slate-800";
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${toneClass}`}>
      <span>{label}</span><span className="font-bold">{value}</span>
    </div>
  );
}

function ImportPreviewModal({ preview, decisions, setDecisions, onRemap, onCancel, onCommit }) {
  const fields = preview.internal_fields || [];
  const detected = preview.detected_headers || [];
  const mapping = preview.mapping || {};
  const missingRequired = REQUIRED_MAPPING_KEYS.filter((k) => !mapping[k]);

  const changeMap = (key, val) => {
    const next = { ...mapping };
    if (val) next[key] = val; else delete next[key];
    onRemap(next);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="roster-import-preview-modal"
    >
      <div
        className="bg-white rounded-lg max-w-5xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold">Import preview</h2>
          <p className="text-sm text-slate-500 mt-1">
            Match each field to a column in your file, then commit. The mapping is
            saved for this upload — change it any time before committing.
          </p>
        </div>

        {/* Column mapping */}
        <div className="p-6 border-b border-slate-200 bg-slate-50/60">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-sm font-semibold">Match your columns</div>
            <div className="text-xs text-slate-500">
              {preview.row_count} data row{preview.row_count === 1 ? "" : "s"} · {detected.length} column{detected.length === 1 ? "" : "s"} detected
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fields.map((f) => {
              const value = mapping[f.key] || "";
              const missing = f.required && !value;
              return (
                <div
                  key={f.key}
                  className={`flex items-center gap-2 p-2 rounded border ${
                    missing ? "border-rose-300 bg-rose-50/40" : "border-slate-200 bg-white"
                  }`}
                >
                  <label className="text-sm font-medium text-slate-700 w-40 shrink-0">
                    {f.label}
                    {f.required && <span className="text-rose-600 ml-0.5">*</span>}
                  </label>
                  <select
                    value={value}
                    onChange={(e) => changeMap(f.key, e.target.value)}
                    data-testid={`roster-import-map-${f.key}`}
                    className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm bg-white"
                  >
                    <option value="">— (skip)</option>
                    {detected.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
          {missingRequired.length > 0 && (
            <div className="mt-3 text-xs text-rose-700 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              Match the required fields (marked <span className="text-rose-600 font-bold">*</span>) before committing.
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">
          {(preview.unmapped_headers && preview.unmapped_headers.length > 0) && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
              <div className="font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" /> {preview.unmapped_headers.length} column(s) will be ignored
              </div>
              <div className="text-amber-800 text-xs">
                Not mapped to any field:{" "}
                {preview.unmapped_headers.map((h) => (
                  <code key={h} className="mx-1 px-1.5 py-0.5 bg-white rounded border border-amber-200">{h}</code>
                ))}
              </div>
            </div>
          )}
          <PreviewSection
            title={`New rows (${preview.new.length})`}
            items={preview.new}
            tone="green"
            renderItem={(item) => (
              <span>
                <strong>{item.incoming.first_name} {item.incoming.last_name}</strong>
                {item.incoming.email && <span className="text-slate-500"> · {item.incoming.email}</span>}
                {item.incoming.team_letter && (
                  <span className="ml-2 px-1.5 py-0.5 bg-slate-900 text-white rounded text-[10px] font-bold">
                    {item.incoming.team_letter}
                  </span>
                )}
              </span>
            )}
          />
          {preview.duplicates.length > 0 && (
            <div>
              <div className="text-sm font-semibold mb-2 text-amber-800">
                Duplicates — already on the roster ({preview.duplicates.length})
              </div>
              <div className="space-y-2">
                {preview.duplicates.map((d) => (
                  <div
                    key={d.existing_id}
                    className="p-3 rounded border border-amber-200 bg-amber-50/50 flex items-center gap-3"
                    data-testid={`roster-import-dup-${d.existing_id}`}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{d.incoming.first_name} {d.incoming.last_name}</div>
                      <div className="text-xs text-slate-500">{d.incoming.email || d.current.email}</div>
                    </div>
                    <select
                      value={decisions[d.existing_id] || "update"}
                      onChange={(e) => setDecisions({ ...decisions, [d.existing_id]: e.target.value })}
                      className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
                      data-testid={`roster-import-decision-${d.existing_id}`}
                    >
                      <option value="update">Update this row</option>
                      <option value="skip">Skip — keep existing</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
          {preview.invalid.length > 0 && (
            <PreviewSection
              title={`Invalid rows (${preview.invalid.length}) — will be skipped`}
              items={preview.invalid}
              tone="red"
              renderItem={(item) => (
                <span>
                  <AlertCircle className="w-3 h-3 inline mr-1 text-rose-600" />
                  Row {item.row_no}: <em>{item.reason}</em>
                </span>
              )}
            />
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="roster-import-cancel-btn">Cancel</Button>
          <Button
            onClick={onCommit}
            disabled={missingRequired.length > 0 || preview.new.length + preview.duplicates.length === 0}
            data-testid="roster-import-commit-btn"
          >
            <Save className="w-4 h-4 mr-1.5" /> Commit import
          </Button>
        </div>
      </div>
    </div>
  );
}

function PreviewSection({ title, items, tone, renderItem }) {
  if (!items || items.length === 0) return null;
  const toneClass = {
    green: "text-emerald-800",
    red: "text-rose-800",
  }[tone] || "text-slate-800";
  return (
    <div>
      <div className={`text-sm font-semibold mb-2 ${toneClass}`}>{title}</div>
      <div className="max-h-48 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
        {items.map((it, i) => (
          <div key={i} className="px-3 py-1.5 text-sm">{renderItem(it)}</div>
        ))}
      </div>
    </div>
  );
}

function RenameTeamsDialog({ teams, teamCount, onClose, onSaved }) {
  const [names, setNames] = useState(() => {
    const m = {};
    TEAM_LETTERS.slice(0, teamCount).forEach((L) => {
      const t = teams.find((x) => x.letter === L);
      m[L] = t?.name || `Team ${L}`;
    });
    return m;
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/teams/rename-batch", { names });
      toast.success("Team names updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update team names");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="roster-rename-teams-modal"
    >
      <div
        className="bg-white rounded-lg max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold">Set team trading names</h2>
          <p className="text-sm text-slate-500 mt-1">
            After Session 2, each team picks a trading name. Enter one per team — every
            member of Team A inherits &quot;Acme Ltd&quot;, Team B inherits their choice, etc.
          </p>
        </div>
        <div className="p-6 space-y-3">
          {TEAM_LETTERS.slice(0, teamCount).map((L) => (
            <div key={L} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold">
                {L}
              </div>
              <span className="text-sm text-slate-500 w-16">is now known as</span>
              <Input
                value={names[L] || ""}
                onChange={(e) => setNames({ ...names, [L]: e.target.value })}
                placeholder={`Team ${L} trading name…`}
                data-testid={`roster-rename-input-${L}`}
                className="flex-1"
              />
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="roster-rename-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save names"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RosterEditDialog({ entry, teamCount, onClose, onSaved }) {
  const isNew = !entry?.id;
  const [form, setForm] = useState({
    first_name: entry.first_name || "",
    last_name: entry.last_name || "",
    email: entry.email || "",
    prid: entry.prid || "",
    department: entry.department || "",
    campus: entry.campus || "",
    gender: entry.gender || "",
    team_letter: entry.team_letter || "",
    dietary: entry.dietary || "",
    notes: entry.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const canSave = form.first_name.trim() && form.last_name.trim();

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/roster", form);
        toast.success(`Added ${form.first_name} ${form.last_name} to the roster`);
      } else {
        await api.patch(`/admin/roster/${entry.id}`, form);
        toast.success("Student updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="roster-edit-modal"
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold">{isNew ? "Add student to the roster" : "Edit student"}</h2>
          <p className="text-sm text-slate-500 mt-1">
            {isNew
              ? "Use this for late arrivals or anyone not on the CRM export. All fields except name are optional."
              : "Fix a typo, add missing dietary info, or move them between teams. Changes save when you click Save."}
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <EditField label="First name *" testid="roster-edit-first_name"
                 value={form.first_name} onChange={(v) => set("first_name", v)} />
          <EditField label="Last name *" testid="roster-edit-last_name"
                 value={form.last_name} onChange={(v) => set("last_name", v)} />
          <EditField label="Email" testid="roster-edit-email" type="email"
                 value={form.email} onChange={(v) => set("email", v)}
                 hint="Match their University email for auto-attach on registration." />
          <EditField label="Student Number (PRID)" testid="roster-edit-prid"
                 value={form.prid} onChange={(v) => set("prid", v)} />
          <EditField label="Department" testid="roster-edit-department"
                 value={form.department} onChange={(v) => set("department", v)} />
          <EditField label="Campus" testid="roster-edit-campus"
                 value={form.campus} onChange={(v) => set("campus", v)}
                 placeholder="Colchester / Southend / Loughton" />
          <EditField label="Gender" testid="roster-edit-gender"
                 value={form.gender} onChange={(v) => set("gender", v)} />
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Team</label>
            <select
              value={form.team_letter}
              onChange={(e) => set("team_letter", e.target.value)}
              data-testid="roster-edit-team_letter"
              className="w-full border border-slate-300 rounded px-2 py-2 text-sm bg-white h-10"
            >
              <option value="">— Unassigned —</option>
              {TEAM_LETTERS.slice(0, teamCount).map((L) => (
                <option key={L} value={L}>Team {L}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <EditField label="Dietary / Allergies" testid="roster-edit-dietary"
                   value={form.dietary} onChange={(v) => set("dietary", v)}
                   placeholder="e.g. Vegan · Gluten-free · Nut allergy · None" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Admin notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              data-testid="roster-edit-notes"
              className="w-full border border-slate-300 rounded px-2 py-2 text-sm bg-white min-h-[64px]"
              placeholder="Anything the admin team needs to remember about this student…"
            />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !canSave} data-testid="roster-edit-save-btn">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : (isNew ? "Add student" : "Save changes")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, placeholder, type = "text", testid, hint }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="h-10"
      />
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function BulkProjectLeadDialog({ teams, teamCount, roster, onClose, onSaved }) {
  // Registered students, grouped by team letter
  const byTeam = {};
  TEAM_LETTERS.slice(0, teamCount).forEach((L) => { byTeam[L] = []; });
  roster.forEach((r) => {
    if (r.team_letter && byTeam[r.team_letter] && r.registered) {
      byTeam[r.team_letter].push(r);
    }
  });
  const [picks, setPicks] = useState(() => {
    const m = {};
    TEAM_LETTERS.slice(0, teamCount).forEach((L) => {
      const current = (byTeam[L] || []).find((r) => r.is_leader);
      m[L] = current?.id || "";
    });
    return m;
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      // For each pick, call set-leader. Sequential to keep server logic simple.
      let n = 0;
      for (const L of TEAM_LETTERS.slice(0, teamCount)) {
        const rid = picks[L];
        if (!rid) continue;
        await api.post(`/admin/roster/${rid}/set-leader`, { is_leader: true });
        n += 1;
      }
      toast.success(`Set Team Leader for ${n} team${n === 1 ? "" : "s"}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save Team Leaders");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="roster-bulk-leader-modal"
    >
      <div
        className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-600" /> Set Team Leaders
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            After Session 1 each team nominates a Team Leader. Pick one registered
            student per team — they become the primary contact.
          </p>
        </div>
        <div className="p-6 space-y-3">
          {TEAM_LETTERS.slice(0, teamCount).map((L) => {
            const team = teams.find((t) => t.letter === L);
            const members = byTeam[L] || [];
            return (
              <div key={L} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold">
                  {L}
                </div>
                <div className="w-32 shrink-0">
                  <div className="text-sm font-medium truncate">{team?.name || `Team ${L}`}</div>
                  <div className="text-[10px] uppercase text-slate-400">{members.length} registered</div>
                </div>
                <select
                  value={picks[L] || ""}
                  onChange={(e) => setPicks({ ...picks, [L]: e.target.value })}
                  data-testid={`roster-bulk-leader-select-${L}`}
                  className="flex-1 border border-slate-300 rounded px-2 py-2 text-sm bg-white h-10"
                  disabled={members.length === 0}
                >
                  <option value="">
                    {members.length === 0 ? "— No registered members yet —" : "— No lead —"}
                  </option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.full_name}{m.is_leader ? " (current lead)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="roster-bulk-leader-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Team Leaders"}
          </Button>
        </div>
      </div>
    </div>
  );
}

